import { ButtonBlinkMode, consumer, GameState, I18NextProvider, inject } from "@spawn/core";
import { INetworkState } from "@spawn/freegames";
import { GameMessageService, GameMessageType } from "@spawn/modern-ui";
import { RevealState } from "@spawn/reveal";
import {
	AutoplayController,
	AutoPlayEvent,
	NetworkService,
	quickStopEvent,
	ReelController,
	ReelSetEvents,
	ReelSetStoppedEvent,
	SlotMachineButtonId,
	SpinController,
	UiEvents,
} from "@spawn/slot-machine";
import { ReelSetName } from "../config/reelSetName";
import { CacheKeys, CacheStorage } from "../utils/cacheStorage";

@consumer
export class FreegameInitialSpinningState extends GameState {
	@inject protected reelController!: ReelController;
	@inject protected networkService!: NetworkService<INetworkState>;
	@inject protected autoplayController!: AutoplayController;
	@inject protected spinController!: SpinController;
	@inject protected gameMessageService!: GameMessageService;
	@inject protected i18n!: I18NextProvider;

	public override async onEnter(): Promise<void> {
		this.subscriptions.add(
			this.eventDispatcher.event$<ReelSetStoppedEvent>(ReelSetEvents.Stopped).subscribe(() => this.onReelSetIdle())
		);

		this.reelController.disableReels(ReelSetName.BaseGame, [1, 3]);

		this.setButtonStates();
		this.addButtonSubscriptions();

		await this.spinController.spin({ actionType: "PSEUDO_SPIN" });
	}

	public onLeave(): void {
		this.reelController.enableReels(ReelSetName.BaseGame, [1, 3]);
		CacheStorage.set(CacheKeys.FirstFGSpinResponse, null);
		this.setGameMessage();
	}

	/** Set and handle all buttons states in this active state. */
	protected setButtonStates(): void {
		this.buttonStateController.setEnabled(
			[
				SlotMachineButtonId.TurboSpin,
				SlotMachineButtonId.MaxBet,
				SlotMachineButtonId.Bet,
				SlotMachineButtonId.Lines,
				SlotMachineButtonId.QuickMenu,
				SlotMachineButtonId.Help,
				SlotMachineButtonId.PayTable,
			],
			false
		);
		this.buttonStateController.setVisible([SlotMachineButtonId.Spin]);

		this.buttonStateController.setBlinkMode(SlotMachineButtonId.all, ButtonBlinkMode.None);

		// Casino specific options.
		this.buttonStateController.setEnabled([SlotMachineButtonId.Spin], this.networkService.isQuickStopAllowed);
		this.buttonStateController.updateState([SlotMachineButtonId.AutoSpin], {
			isVisible: this.networkService.isAutoPlayAllowed,
			isEnabled: this.autoplayController.isAutoSpinEnabled,
		});
	}

	private setGameMessage(): void {
		this.gameMessageService.setCustomGameMessage(
			this.i18n.t("freegames.currentRound", {
				currentRound: 1,
				totalRounds: 2,
			}),
			GameMessageType.Primary
		);
	}

	/** Subscribe to all possible button actions. */
	protected addButtonSubscriptions(): void {
		this.subscriptions.add(
			this.eventDispatcher.event$(UiEvents.Spin).subscribe(() => {
				if (this.networkService.isQuickStopAllowed) {
					this.reelController.quickspin();
					// Track quick stop event for casino bridges.
					this.eventDispatcher.dispatch(quickStopEvent());
				}
			})
		);
		this.subscriptions.add(
			this.eventDispatcher.event$<AutoPlayEvent>(UiEvents.AutoPlay).subscribe(({ payload }) => {
				// When autoplay is deactivated, it shouldn't be possible to activate it againg during the current spin.
				if (payload.isActive === false) {
					this.buttonStateController.updateState([SlotMachineButtonId.AutoSpin], {
						isVisible: true,
						isEnabled: false,
					});
				}
			})
		);
	}

	protected onReelSetIdle(): void {
		this.stateComplete(RevealState);
	}
}
